/**
 * CreditOperationsPage — Tela de Operações de Crédito.
 * 1 linha = 1 operação (GROUP BY credit_operation_id na title_debt, via settlement REST).
 * Ações: ver títulos de dívida (settlement) e ver invoices (billing).
 * TODO (combinado): credit_operation_id → nº contrato-aditamento como identificador principal.
 */
import { useState, useMemo, useCallback } from 'react';
import PageList from '../../templates/PageList';
import appConfig from '../../../config/app-config';
import { FilterBuilder } from '../../molecules/FilterBuilder';
import { AppliedFilter } from '../../molecules/FilterBuilder/types';
import { operationsFilterSchema, serializeFilters } from './constants/filterSchemas';
import OperationTitleDebtsModal from './modals/OperationTitleDebtsModal';
import OperationInvoicesModal from './modals/OperationInvoicesModal';
import { formatBRL } from '../../../utils/currencyUtils';
import { FaListOl, FaFileInvoiceDollar } from 'react-icons/fa';

const modalityLabels: Record<string, string> = {
    LOAN: 'Empréstimo',
    COMMON_CPRF: 'CPRF',
    CESSION_WITHOUT_RETURN: 'Cessão s/ regresso',
    CESSION_WITH_RETURN: 'Cessão c/ regresso',
};

const referenceLabels: Record<string, string> = {
    INSTALLMENT: 'Parcela',
    RECEIVABLE: 'Recebível',
};

const CreditOperationsPage = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    const [currentSortField, setCurrentSortField] = useState('contract_number');
    const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [activeFilters, setActiveFilters] = useState<AppliedFilter[]>([]);
    const [titleDebtsOperation, setTitleDebtsOperation] = useState<any>(null);
    const [invoicesOperation, setInvoicesOperation] = useState<any>(null);

    const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

    const sortParam = currentSortOrder === 'DESC' ? `-${currentSortField}` : currentSortField;
    const params = useMemo(() => {
        const p = new URLSearchParams({
            page: String(currentPage + 1),
            size: String(currentPageSize),
            sort: sortParam,
        });
        const filterParams = serializeFilters(activeFilters);
        filterParams.forEach((value, key) => p.set(key, value));
        return p;
    }, [currentPage, currentPageSize, sortParam, activeFilters]);

    const url = `${API_URL}/operations?${params.toString()}`;

    const handleFilterSearch = useCallback((filters: AppliedFilter[]) => {
        setActiveFilters(filters);
        setCurrentPage(0);
    }, []);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API_KEY || '' }
    ], [API_KEY]);

    const tableHeaders = useMemo(() => [
        { id: 'credit_operation_id', title: 'Operação', canSort: false },
        { id: 'contract_number', title: 'Contrato', canSort: true, field: 'contract_number' },
        { id: 'amendment_number', title: 'Aditamento', canSort: true, field: 'amendment_number' },
        { id: 'contract_modality', title: 'Modalidade', canSort: false },
        { id: 'business_reference', title: 'Referência', canSort: false },
        { id: 'amount', title: 'Valor', canSort: true, field: 'amount' },
        { id: 'paid_amount', title: 'Pago', canSort: true, field: 'paid_amount' },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    const renderRows = (rows: any[]) => {
        return rows.map((row: any) => (
            <tr key={row.credit_operation_id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm text-gray-500 font-mono" title={row.credit_operation_id}>
                    {row.credit_operation_id?.slice(0, 8)}
                </td>
                <td className="px-4 py-2 text-sm font-medium">{row.contract_number || '-'}</td>
                <td className="px-4 py-2 text-sm text-center">{row.amendment_number >= 0 ? row.amendment_number : '-'}</td>
                <td className="px-4 py-2 text-sm">{modalityLabels[row.contract_modality] || row.contract_modality || '-'}</td>
                <td className="px-4 py-2 text-sm">{referenceLabels[row.business_reference] || row.business_reference || '-'}</td>
                <td className="px-4 py-2 text-sm text-right">{formatBRL(row.amount)}</td>
                <td className={`px-4 py-2 text-sm text-right ${row.paid_amount > 0 ? 'text-green-600 font-medium' : ''}`}>
                    {formatBRL(row.paid_amount)}
                </td>
                <td className="px-4 py-2 text-sm whitespace-nowrap">
                    <button
                        className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 mr-1 inline-flex items-center gap-1"
                        onClick={() => setTitleDebtsOperation(row)}
                        title="Ver títulos de dívida"
                    ><FaListOl /> Títulos ({row.title_debts_count})</button>
                    <button
                        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 inline-flex items-center gap-1"
                        onClick={() => setInvoicesOperation(row)}
                        title="Ver faturas (billing)"
                    ><FaFileInvoiceDollar /> Faturas</button>
                </td>
            </tr>
        ));
    };

    return (
        <>
            <PageList
                pageTitle="Operações de Crédito"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Financeiro', url: '#' },
                    { label: 'Operações de Crédito', url: '/settlement/operations' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={currentSortField}
                tableOrderSort={currentSortOrder}
                onTableSort={(field: string, order: 'ASC' | 'DESC') => { setCurrentSortField(field); setCurrentSortOrder(order); }}
                renderRows={renderRows}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={(p: number) => setCurrentPage(p)}
                onItemsPerPageChange={(s: number) => { setCurrentPageSize(s); setCurrentPage(0); }}
                itemsLabel="operações"
            >
                {{
                    filterSlot: (
                        <FilterBuilder
                            schema={operationsFilterSchema}
                            onSearch={handleFilterSearch}
                        />
                    ),
                    controlSlot: (<div></div>),
                }}
            </PageList>
            {titleDebtsOperation && (
                <OperationTitleDebtsModal
                    operation={titleDebtsOperation}
                    onClose={() => setTitleDebtsOperation(null)}
                />
            )}
            {invoicesOperation && (
                <OperationInvoicesModal
                    operation={invoicesOperation}
                    onClose={() => setInvoicesOperation(null)}
                />
            )}
        </>
    );
};

export default CreditOperationsPage;
